const PREVIEW_GUARD = `<script data-wembii-preview-guard>
(function () {
  document.querySelectorAll('meta[http-equiv="refresh" i]').forEach(function (m) {
    m.remove();
  });

  document.addEventListener(
    "click",
    function (e) {
      var el = e.target.closest("a[href]");
      if (!el) return;
      var href = (el.getAttribute("href") || "").trim();
      if (!href || href.startsWith("#")) return;
      e.preventDefault();
      e.stopPropagation();
      if (/^https?:\\/\\//i.test(href) || href.startsWith("//")) {
        window.open(href, "_blank", "noopener,noreferrer");
      }
    },
    true
  );

  document.addEventListener(
    "submit",
    function (e) {
      e.preventDefault();
    },
    true
  );
})();
</script>`;

export interface WebsitePageWidgetOptions {
  companyId?: string;
  includeWhatsapp?: boolean;
  formId?: string | null;
}

const resolveEmbeddedScriptUrl = (domain?: string) => {
  if (!domain) return null;
  const trimmed = String(domain).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

const stripWembiiWidgetScripts = (html: string) =>
  String(html)
    .replace(/<script\b[^>]*\bsrc=["'][^"']*embedded-whatsapp[^"']*["'][^>]*>\s*<\/script>\s*/gi, "")
    .replace(/<script\b[^>]*>\s*window\.loadWembiiWhatsapp\([^)]*\)\s*<\/script>\s*/gi, "")
    .replace(/<script\b[^>]*\bsrc=["'][^"']*embedded-form[^"']*["'][^>]*>\s*<\/script>\s*/gi, "")
    .replace(/<script\b[^>]*>\s*window\.loadWembiiForm\([^)]*\)\s*<\/script>\s*/gi, "");

/** Injects Wembii form / WhatsApp widget scripts (same contract as backend renderer). */
export function injectWebsitePageWidgets(html: string, options: WebsitePageWidgetOptions = {}): string {
  let doc = stripWembiiWidgetScripts(html);
  const companyId = options.companyId;
  if (!companyId) return doc;

  const scripts: string[] = [];
  const wantsForm = /wembii-lith-form|id=["']wembii-lith-form["']/i.test(doc);

  if (wantsForm && options.formId) {
    const formDomain = resolveEmbeddedScriptUrl(process.env.REACT_APP_EMBEDDED_FORM_DOMAIN);
    if (formDomain) {
      scripts.push(`<script src="${formDomain}"></script>`);
      scripts.push(
        `<script>window.loadWembiiForm("${btoa(String(companyId))}", "${btoa(String(options.formId))}")</script>`
      );
    }
  }

  if (options.includeWhatsapp) {
    const whatsappDomain = resolveEmbeddedScriptUrl(process.env.REACT_APP_EMBEDDED_WHATSAPP_DOMAIN);
    if (whatsappDomain) {
      scripts.push(`<script src="${whatsappDomain}"></script>`);
      scripts.push(`<script>window.loadWembiiWhatsapp("${btoa(String(companyId))}")</script>`);
    }
  }

  if (!scripts.length) return doc;

  const block = scripts.join("\n");
  if (doc.includes("</body>")) {
    return doc.replace("</body>", `${block}\n</body>`);
  }
  return `${doc}\n${block}`;
}

/** Wraps HTML for safe in-app preview — prevents iframe from loading the Wembii SPA. */
export function prepareHtmlPreviewDocument(
  html: string,
  widgets: WebsitePageWidgetOptions = {}
): string {
  let doc = injectWebsitePageWidgets(String(html || "").trim(), widgets);
  if (!doc) {
    return `<!DOCTYPE html><html><head>${PREVIEW_GUARD}</head><body></body></html>`;
  }

  doc = doc.replace(/<base\b[^>]*>/gi, "");
  doc = doc.replace(/<meta\b[^>]*http-equiv=["']refresh["'][^>]*>/gi, "");

  if (/<head[^>]*>/i.test(doc)) {
    return doc.replace(/<head[^>]*>/i, (match) => `${match}${PREVIEW_GUARD}`);
  }
  if (/<html[^>]*>/i.test(doc)) {
    return doc.replace(/<html[^>]*>/i, (match) => `${match}<head>${PREVIEW_GUARD}</head>`);
  }

  return `<!DOCTYPE html><html><head>${PREVIEW_GUARD}</head><body>${doc}</body></html>`;
}

export const HTML_PREVIEW_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";
